import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const pickData = (res) => res?.data ?? res?.entry ?? res;
const key = (x) => String(x ?? "");

export const counterApi = createApi({
  reducerPath: "counterApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["COUNTERS", "COUNTER"],
  endpoints: (builder) => ({
    // LIST
    getCounters: builder.query({
      query: ({ page = 1, limit = 20, q = "", branchId = "", active = "" } = {}) => {
        const params = { page, limit };
        if (q) params.q = q;
        if (branchId) params.branchId = branchId;
        if (active === true || active === false) params.active = String(active);
        return { url: "/counters", method: "GET", params };
      },
      providesTags: (res) =>
        res?.data
          ? [
              ...res.data.map((c) => ({ type: "COUNTER", id: key(c._id) })),
              { type: "COUNTERS", id: "LIST" },
            ]
          : [{ type: "COUNTERS", id: "LIST" }],
      keepUnusedDataFor: 60,
    }),

    getCountersAll: builder.query({
      query: ({ page = 1, limit = 200, search = "", branchId, active } = {}) => {
        const params = { page, limit };
        if (search) params.q = search;
        if (branchId) params.branchId = branchId;
        if (typeof active === "boolean") params.active = active;
        return { url: "/counters/all", params };
      },
      providesTags: ["Counter"],
    }),
  
    // GET ONE
    getCounterById: builder.query({
      query: (id) => `/counters/${id}`,
      providesTags: (_res, _err, id) => [{ type: "COUNTER", id: key(id) }],
    }),

    // CREATE
    addCounter: builder.mutation({
      query: (payload) => ({ url: "/counters", method: "POST", body: payload }),
      invalidatesTags: [{ type: "COUNTERS", id: "LIST" }],
    }),

    // UPDATE
    updateCounter: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/counters/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "COUNTER", id: key(id) },
        { type: "COUNTERS", id: "LIST" },
      ],
    }),

    // DELETE
    deleteCounter: builder.mutation({
      query: (id) => ({ url: `/counters/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "COUNTERS", id: "LIST" }],
    }),

    // ACTIVATE / DEACTIVATE (single)
    activateCounter: builder.mutation({
      query: (id) => ({ url: `/counters/${id}/activate`, method: "PATCH" }),
      invalidatesTags: (_res, _err, id) => [
        { type: "COUNTER", id: key(id) },
        { type: "COUNTERS", id: "LIST" },
      ],
    }),
    deactivateCounter: builder.mutation({
      query: (id) => ({ url: `/counters/${id}/deactivate`, method: "PATCH" }),
      invalidatesTags: (_res, _err, id) => [
        { type: "COUNTER", id: key(id) },
        { type: "COUNTERS", id: "LIST" },
      ],
    }),

    // -------- BULK --------
    deleteCountersBulk: builder.mutation({
      query: (ids) => ({
        url: "/counters/bulk",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: [{ type: "COUNTERS", id: "LIST" }],
    }),
    activateCountersBulk: builder.mutation({
      query: (ids) => ({
        url: "/counters/bulk/activate",
        method: "PATCH",
        body: { ids },
      }),
      invalidatesTags: [{ type: "COUNTERS", id: "LIST" }],
    }),
    deactivateCountersBulk: builder.mutation({
      query: (ids) => ({
        url: "/counters/bulk/deactivate",
        method: "PATCH",
        body: { ids },
      }),
      invalidatesTags: [{ type: "COUNTERS", id: "LIST" }],
    }),
  }),
});

export const {
  useGetCountersQuery,
  useGetCountersAllQuery, 
  useGetCounterByIdQuery,
  useAddCounterMutation,
  useUpdateCounterMutation,
  useDeleteCounterMutation,
  useActivateCounterMutation,
  useDeactivateCounterMutation,
  useDeleteCountersBulkMutation,
  useActivateCountersBulkMutation,
  useDeactivateCountersBulkMutation,
} = counterApi;

export default counterApi;
